import { Router } from "express";
import { z } from "zod";
import { and, asc, desc, eq, inArray, sql, type SQL } from "drizzle-orm";
import { db } from "../../db/index";
import { leaves, leaveTypes, employeeLeaveQuotas } from "../../db/schema/leave";
import { employeeDetails } from "../../db/schema/employee";
import { users } from "../../db/schema/identity";
import { alias } from "drizzle-orm/mysql-core";
import { requireAuth, requirePermission, scopeCondition, SCOPE } from "../../rbac/index";
import { getEffectivePermissions } from "../../rbac/effective-permissions";
import { pad, companyTodayStr, companySettings, leaveWindow } from "./leave-math";

/**
 * B4 — Leave management (reskin-docs/04). Ported line-by-line from the legacy truth:
 *   - apply / approve / reject / cancel: V2 LeaveController L163–413 (the team's CURRENT
 *     behaviour — "simplified path 2026-06-03": ONE block (duplicate same-date) + HR-policy
 *     probation/notice guardrails; eligibility/quota deliberately not enforced at apply).
 *   - balance window + half-day weighting: LeaveType::byUser L91–155 (remaining is COMPUTED,
 *     entitlement − approved-used-in-window; half day counts 0.5). Our schema stores no
 *     leaves_remaining column — matches the model.
 *   - probation/notice: employmentLeaveWindows L370–392 + leaveBlockReason L398–413.
 * Company-scoped + scope-filtered role-by-role (view_leave). Money/economics N/A here.
 */
export const leaveRouter = Router();
leaveRouter.use(requireAuth);

const asDateStr = (v: unknown): string | null =>
  v == null ? null : v instanceof Date ? v.toISOString().slice(0, 10) : String(v).slice(0, 10);
const ddMonYyyy = (iso: string) => {
  const p = iso.split("-");
  const y = Number(p[0]), m = Number(p[1]), d = Number(p[2]);
  const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${pad(d)} ${MON[(m - 1 + 12) % 12]} ${y}`;
};

/** Probation/notice windows (V2 employmentLeaveWindows). probEnd = explicit, else joining+3mo. */
async function employmentWindows(userId: number): Promise<{ probEnd: string | null; noticeStart: string | null; noticeEnd: string | null }> {
  const row = await db
    .select({
      joiningDate: employeeDetails.joiningDate,
      probationEndDate: employeeDetails.probationEndDate,
      noticeStart: employeeDetails.noticePeriodStartDate,
      noticeEnd: employeeDetails.noticePeriodEndDate,
    })
    .from(employeeDetails)
    .where(eq(employeeDetails.userId, userId))
    .limit(1);
  const ed = row[0];
  let probEnd: string | null = null;
  let noticeStart: string | null = null;
  let noticeEnd: string | null = null;
  if (ed) {
    if (ed.probationEndDate) probEnd = asDateStr(ed.probationEndDate);
    else if (ed.joiningDate) {
      const j = ed.joiningDate instanceof Date ? ed.joiningDate : new Date(ed.joiningDate);
      const d = new Date(Date.UTC(j.getUTCFullYear(), j.getUTCMonth() + 3, j.getUTCDate()));
      probEnd = d.toISOString().slice(0, 10);
    }
    if (ed.noticeStart) {
      noticeStart = asDateStr(ed.noticeStart);
      noticeEnd = ed.noticeEnd ? asDateStr(ed.noticeEnd) : null;
    }
  }
  return { probEnd, noticeStart, noticeEnd };
}

/** Block reason if leaveDate falls in probation/notice (V2 leaveBlockReason), else null. */
function blockReason(w: { probEnd: string | null; noticeStart: string | null; noticeEnd: string | null }, leaveDate: string): string | null {
  if (w.probEnd && leaveDate <= w.probEnd)
    return `Leave can't be applied during your probation period (until ${ddMonYyyy(w.probEnd)}). Please contact HR for any exception.`;
  if (w.noticeStart && leaveDate >= w.noticeStart && (!w.noticeEnd || leaveDate <= w.noticeEnd))
    return `Leave can't be taken during your notice period${w.noticeEnd ? ` (until ${ddMonYyyy(w.noticeEnd)})` : ""}. Please coordinate your last working days with HR.`;
  return null;
}

async function isApprover(userId: number): Promise<boolean> {
  const perms = await getEffectivePermissions(userId);
  return perms["approve_or_reject_leaves"] === SCOPE.ALL;
}

/** Computed balances per leave-type for a user (LeaveType::byUser remaining = entitlement − used). */
async function computeBalances(companyId: number, userId: number) {
  const s = await companySettings(companyId);
  const today = companyTodayStr(s.timezone);
  const jd = await db
    .select({ joiningDate: employeeDetails.joiningDate })
    .from(employeeDetails)
    .where(eq(employeeDetails.userId, userId))
    .limit(1);
  const { from, to } = leaveWindow(s, jd[0]?.joiningDate ?? null, today);

  const quotas = await db
    .select({
      leaveTypeId: employeeLeaveQuotas.leaveTypeId,
      entitlement: employeeLeaveQuotas.noOfLeaves,
      typeName: leaveTypes.typeName,
      color: leaveTypes.color,
      paid: leaveTypes.paid,
    })
    .from(employeeLeaveQuotas)
    .innerJoin(leaveTypes, eq(leaveTypes.id, employeeLeaveQuotas.leaveTypeId))
    .where(eq(employeeLeaveQuotas.userId, userId));

  const used = await db
    .select({
      leaveTypeId: leaves.leaveTypeId,
      used: sql<number>`COALESCE(SUM(CASE WHEN ${leaves.duration} = 'half day' THEN 0.5 ELSE 1 END), 0)`,
    })
    .from(leaves)
    .where(
      and(
        eq(leaves.userId, userId),
        eq(leaves.status, "approved"),
        sql`${leaves.leaveDate} >= ${from}`,
        sql`${leaves.leaveDate} < ${to}`,
      ),
    )
    .groupBy(leaves.leaveTypeId);
  const usedMap = new Map(used.map((u) => [u.leaveTypeId, Number(u.used)]));

  return {
    window: { from, to },
    balances: quotas.map((q) => {
      const u = usedMap.get(q.leaveTypeId) ?? 0;
      const ent = Number(q.entitlement);
      return {
        leaveTypeId: q.leaveTypeId,
        typeName: q.typeName,
        color: q.color,
        paid: q.paid,
        entitlement: ent,
        used: u,
        remaining: Math.max(0, +(ent - u).toFixed(2)),
      };
    }),
  };
}

/* ───────────────────────────── List + KPIs ───────────────────────────── */

/** GET /api/leaves — scope-filtered list (V2 index). Approvers default to the queue. */
leaveRouter.get("/leaves", requirePermission("view_leave"), async (req, res) => {
  const { userId, companyId } = req.auth!;
  const scope = req.scope as 1 | 2 | 3 | 4 | 5;
  const approver = await isApprover(userId);
  const view = (req.query.view as string) || (approver ? "queue" : "mine");
  const status = (req.query.status as string) || "all";
  const sortKey = (req.query.sort as string) || "leave_date";
  const dir = (req.query.dir as string) === "asc" ? asc : desc;
  const perPage = [10, 25, 50, 100].includes(Number(req.query.per_page)) ? Number(req.query.per_page) : 25;
  const page = Math.max(1, Number(req.query.page) || 1);

  const conds: SQL[] = [eq(leaves.companyId, companyId)];
  const sc = scopeCondition(scope, userId, { addedBy: leaves.addedBy, owner: leaves.userId });
  if (sc === "DENY") conds.push(sql`1 = 0`);
  else if (sc) conds.push(sc);
  // "mine" view (or no ALL/BOTH reach) restricts to the requester's own rows.
  if (view === "mine") conds.push(eq(leaves.userId, userId));
  if (status !== "all" && ["pending", "approved", "rejected"].includes(status)) conds.push(eq(leaves.status, status as "pending"));

  const sortCol =
    sortKey === "applied" ? leaves.createdAt : sortKey === "status" ? leaves.status : sortKey === "duration" ? leaves.duration : leaves.leaveDate;

  const applicant = alias(users, "applicant");
  const rows = await db
    .select({
      id: leaves.id,
      leaveDate: leaves.leaveDate,
      duration: leaves.duration,
      halfDayType: leaves.halfDayType,
      status: leaves.status,
      reason: leaves.reason,
      rejectReason: leaves.rejectReason,
      applyMode: leaves.applyMode,
      urgency: leaves.urgency,
      createdAt: leaves.createdAt,
      userId: leaves.userId,
      employee: applicant.name,
      typeId: leaveTypes.id,
      typeName: leaveTypes.typeName,
      color: leaveTypes.color,
    })
    .from(leaves)
    .leftJoin(applicant, eq(applicant.id, leaves.userId))
    .leftJoin(leaveTypes, eq(leaveTypes.id, leaves.leaveTypeId))
    .where(and(...conds))
    .orderBy(dir(sortCol))
    .limit(perPage)
    .offset((page - 1) * perPage);

  const totalRow = await db.select({ n: sql<number>`COUNT(*)` }).from(leaves).where(and(...conds));
  const total = Number(totalRow[0]?.n ?? 0);

  // KPIs over the same scoped base (status-independent).
  const baseConds: SQL[] = [eq(leaves.companyId, companyId)];
  if (sc === "DENY") baseConds.push(sql`1 = 0`);
  else if (sc) baseConds.push(sc);
  if (view === "mine") baseConds.push(eq(leaves.userId, userId));
  const kpiRow = await db
    .select({
      pending: sql<number>`SUM(CASE WHEN ${leaves.status} = 'pending' THEN 1 ELSE 0 END)`,
      approved: sql<number>`SUM(CASE WHEN ${leaves.status} = 'approved' THEN 1 ELSE 0 END)`,
      rejected: sql<number>`SUM(CASE WHEN ${leaves.status} = 'rejected' THEN 1 ELSE 0 END)`,
      total: sql<number>`COUNT(*)`,
    })
    .from(leaves)
    .where(and(...baseConds));
  const k = kpiRow[0];

  const canApprove = (await getEffectivePermissions(userId))["approve_or_reject_leaves"] === SCOPE.ALL;

  res.json({
    rows: rows.map((r) => ({ ...r, leaveDate: asDateStr(r.leaveDate) })),
    kpis: {
      pending: Number(k?.pending ?? 0),
      approved: Number(k?.approved ?? 0),
      rejected: Number(k?.rejected ?? 0),
      total: Number(k?.total ?? 0),
    },
    page,
    perPage,
    total,
    totalPages: Math.max(1, Math.ceil(total / perPage)),
    isApprover: approver,
    canApprove,
    view,
  });
});

/** GET /api/leaves/options — leave types + (approver only) employees + the requester's own note. */
leaveRouter.get("/leaves/options", requirePermission("view_leave"), async (req, res) => {
  const { userId, companyId } = req.auth!;
  const approver = await isApprover(userId);
  const types = await db
    .select({ id: leaveTypes.id, typeName: leaveTypes.typeName, color: leaveTypes.color })
    .from(leaveTypes)
    .where(eq(leaveTypes.companyId, companyId))
    .orderBy(asc(leaveTypes.typeName));
  const employees = approver
    ? await db.select({ id: users.id, name: users.name }).from(users).where(eq(users.companyId, companyId)).orderBy(asc(users.name))
    : [];

  // Own probation/notice note (mirrors V2 index $leavePolicyNote) — approvers exempt.
  let policyNote: string | null = null;
  if (!approver) {
    const s = await companySettings(companyId);
    const today = companyTodayStr(s.timezone);
    const w = await employmentWindows(userId);
    if (w.probEnd && today <= w.probEnd)
      policyNote = `You're in your probation period (until ${ddMonYyyy(w.probEnd)}) — leave can't be applied until then. Please contact HR for any exception.`;
    else if (w.noticeStart && today >= w.noticeStart && (!w.noticeEnd || today <= w.noticeEnd))
      policyNote = `You're serving your notice period${w.noticeEnd ? ` (until ${ddMonYyyy(w.noticeEnd)})` : ""} — leave can't be applied during notice. Please coordinate with HR.`;
  }

  const ownBalances = await computeBalances(companyId, userId);
  res.json({ types, employees, isApprover: approver, policyNote, balances: ownBalances.balances });
});

/** GET /api/leaves/balances(/:userId) — computed balances (own, or anyone for approvers). */
async function balancesHandler(req: import("express").Request, res: import("express").Response) {
  const { userId, companyId } = req.auth!;
  const target = req.params.userId ? Number(req.params.userId) : userId;
  if (target !== userId && !(await isApprover(userId)))
    return res.status(403).json({ error: "Forbidden" });
  res.json(await computeBalances(companyId, target));
}
leaveRouter.get("/leaves/balances", requirePermission("view_leave"), balancesHandler);
leaveRouter.get("/leaves/balances/:userId", requirePermission("view_leave"), balancesHandler);

/* ───────────────────────────── Apply ───────────────────────────── */

const applySchema = z.object({
  user_id: z.coerce.number().optional(),
  leave_type_id: z.coerce.number(),
  leave_date: z.string().min(1),
  duration: z.enum(["single", "first_half", "second_half"]).default("single"),
  reason: z.string().default(""),
  status: z.enum(["pending", "approved", "rejected"]).optional(),
  apply_mode: z.enum(["portal", "email", "whatsapp", "verbal"]).optional(),
  urgency: z.enum(["planned", "emergency"]).optional(),
});

/** POST /api/leaves — apply (V2 apply L163–267). add_leave ALL/ADDED. */
leaveRouter.post("/leaves", requirePermission("add_leave"), async (req, res) => {
  const { userId, companyId } = req.auth!;
  if (req.scope !== SCOPE.ALL && req.scope !== SCOPE.ADDED)
    return res.status(403).json({ error: "You don't have permission to apply for leave." });

  const parsed = applySchema.safeParse(req.body);
  if (!parsed.success) return res.status(422).json({ error: "Invalid payload", details: parsed.error.flatten() });
  const b = parsed.data;
  if (b.leave_type_id <= 0) return res.status(422).json({ error: "Pick a leave type." });
  if (!b.reason.trim()) return res.status(422).json({ error: "Reason is required." });

  const approver = await isApprover(userId);
  let targetUser = approver ? b.user_id ?? userId : userId; // non-approvers can only self-apply

  // Parse date — accept Y-m-d or d-m-Y.
  let leaveDate: string | null = null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(b.leave_date)) leaveDate = b.leave_date;
  else if (/^\d{2}-\d{2}-\d{4}$/.test(b.leave_date)) {
    const [d, m, y] = b.leave_date.split("-");
    leaveDate = `${y}-${m}-${d}`;
  }
  if (!leaveDate) return res.status(422).json({ error: "Date format not recognised — use DD-MM-YYYY." });

  // THE one block: no duplicate pending/approved leave on the same date for the same user.
  const dup = await db
    .select({ status: leaves.status })
    .from(leaves)
    .where(and(eq(leaves.userId, targetUser), eq(leaves.leaveDate, leaveDate), inArray(leaves.status, ["pending", "approved"])))
    .limit(1);
  if (dup[0])
    return res.status(422).json({
      error: `You already have a ${dup[0].status} leave on ${leaveDate.split("-").reverse().join("-")}. Cancel or wait for it before applying again.`,
    });

  // HR policy §8/§11 — probation/notice block (employees only; approvers override on-behalf).
  if (!approver) {
    const reason = blockReason(await employmentWindows(targetUser), leaveDate);
    if (reason) return res.status(422).json({ error: reason });
  }

  const dbDuration = b.duration === "single" ? "single" : "half day";
  const halfDayType = b.duration === "single" ? null : b.duration;
  let status: "pending" | "approved" | "rejected" = "pending";
  if (approver && b.status) status = b.status;

  await db.insert(leaves).values({
    companyId,
    userId: targetUser,
    leaveTypeId: b.leave_type_id,
    duration: dbDuration,
    halfDayType,
    leaveDate,
    reason: b.reason.trim(),
    status,
    paid: false,
    overUtilized: false,
    applyMode: b.apply_mode ?? "portal",
    urgency: b.urgency ?? "planned",
    addedBy: userId,
    approvedBy: status !== "pending" ? userId : null,
    approvedAt: status !== "pending" ? new Date() : null,
  });
  res.json({ ok: true, message: "Leave request submitted." });
});

/* ───────────────────────── Approve / Reject / Cancel ───────────────────────── */

/** Shared gate: approve_or_reject_leaves=ALL OR the caller is the applicant's reporting manager. */
async function canActOn(userId: number, leaveUserId: number): Promise<boolean> {
  const perms = await getEffectivePermissions(userId);
  if (perms["approve_or_reject_leaves"] === SCOPE.ALL) return true;
  const mgr = await db
    .select({ id: employeeDetails.id })
    .from(employeeDetails)
    .where(and(eq(employeeDetails.userId, leaveUserId), eq(employeeDetails.reportingTo, userId)))
    .limit(1);
  return !!mgr[0];
}

async function setStatus(req: import("express").Request, res: import("express").Response, newStatus: "approved" | "rejected", reason: string) {
  const { userId, companyId } = req.auth!;
  const id = Number(req.params.id);
  const row = await db.select({ userId: leaves.userId }).from(leaves).where(and(eq(leaves.id, id), eq(leaves.companyId, companyId))).limit(1);
  if (!row[0]) return res.status(404).json({ error: "Leave not found" });
  if (userId === row[0].userId) return res.status(403).json({ error: "You cannot act on your own leave." });
  if (!(await canActOn(userId, row[0].userId)))
    return res.status(403).json({ error: "You don't have permission to approve / reject this leave." });

  await db
    .update(leaves)
    .set({
      status: newStatus,
      approvedBy: userId,
      approvedAt: new Date(),
      ...(newStatus === "rejected" ? { rejectReason: reason } : reason ? { approveReason: reason } : {}),
    })
    .where(eq(leaves.id, id));
  res.json({ ok: true, message: newStatus === "approved" ? "Leave approved." : "Leave rejected.", status: newStatus });
}

/** POST /api/leaves/:id/approve */
leaveRouter.post("/leaves/:id/approve", requirePermission("view_leave"), (req, res) =>
  setStatus(req, res, "approved", String(req.body?.approveReason ?? "").trim()),
);

/** POST /api/leaves/:id/reject — reason required (V2 reject L285). */
leaveRouter.post("/leaves/:id/reject", requirePermission("view_leave"), (req, res) => {
  const reason = String(req.body?.reason ?? "").trim();
  if (!reason) return res.status(422).json({ error: "Please provide a reason for rejection." });
  return setStatus(req, res, "rejected", reason);
});

/** DELETE /api/leaves/:id — self-cancel a pending leave (V2 cancel L300). */
leaveRouter.delete("/leaves/:id", requirePermission("view_leave"), async (req, res) => {
  const { userId, companyId } = req.auth!;
  const id = Number(req.params.id);
  const row = await db.select({ userId: leaves.userId, status: leaves.status }).from(leaves).where(and(eq(leaves.id, id), eq(leaves.companyId, companyId))).limit(1);
  if (!row[0]) return res.status(404).json({ error: "Leave not found" });
  if (userId !== row[0].userId) return res.status(403).json({ error: "You can only cancel your own leaves." });
  if (row[0].status !== "pending") return res.status(422).json({ error: "Only pending leaves can be cancelled." });
  await db.delete(leaves).where(eq(leaves.id, id));
  res.json({ ok: true, message: "Leave cancelled." });
});

/* ───────────────────────────── Leave Types (settings) ───────────────────────────── */

const onlyManage = [
  requirePermission("manage_leave_setting"),
  (req: import("express").Request, res: import("express").Response, next: import("express").NextFunction) =>
    req.scope === SCOPE.ALL ? next() : res.status(403).json({ error: "Forbidden", permission: "manage_leave_setting" }),
];

const typeSchema = z.object({
  type_name: z.string().min(1).max(255),
  color: z.string().min(1).max(20).default("#6366f1"),
  no_of_leaves: z.coerce.number().min(0).default(0),
  paid: z.coerce.boolean().default(true),
  monthly_limit: z.coerce.number().min(0).default(0),
  leavetype: z.enum(["monthly", "yearly"]).default("yearly"),
  unused_leave: z.enum(["lapse", "carry forward", "paid"]).default("lapse"),
  allowed_probation: z.coerce.boolean().default(false),
  allowed_notice: z.coerce.boolean().default(false),
  over_utilization: z.enum(["not_allowed", "allow_paid", "allow_unpaid"]).default("not_allowed"),
});

/** GET /api/leave-types — full list with per-type headcount + entitlement (settings). */
leaveRouter.get("/leave-types", ...onlyManage, async (req, res) => {
  const companyId = req.auth!.companyId;
  const rows = await db
    .select({
      id: leaveTypes.id,
      typeName: leaveTypes.typeName,
      color: leaveTypes.color,
      noOfLeaves: leaveTypes.noOfLeaves,
      paid: leaveTypes.paid,
      monthlyLimit: leaveTypes.monthlyLimit,
      leavetype: leaveTypes.leavetype,
      unusedLeave: leaveTypes.unusedLeave,
      allowedProbation: leaveTypes.allowedProbation,
      allowedNotice: leaveTypes.allowedNotice,
      overUtilization: leaveTypes.overUtilization,
      assigned: sql<number>`(SELECT COUNT(*) FROM employee_leave_quotas q WHERE q.leave_type_id = ${leaveTypes.id})`,
    })
    .from(leaveTypes)
    .where(eq(leaveTypes.companyId, companyId))
    .orderBy(asc(leaveTypes.typeName));
  res.json({ rows: rows.map((r) => ({ ...r, noOfLeaves: Number(r.noOfLeaves), monthlyLimit: Number(r.monthlyLimit) })) });
});

/** POST /api/leave-types — create + seed a quota row for every company employee. */
leaveRouter.post("/leave-types", ...onlyManage, async (req, res) => {
  const companyId = req.auth!.companyId;
  const parsed = typeSchema.safeParse(req.body);
  if (!parsed.success) return res.status(422).json({ error: "Invalid payload", details: parsed.error.flatten() });
  const b = parsed.data;
  const [ins] = await db
    .insert(leaveTypes)
    .values({
      companyId,
      typeName: b.type_name,
      color: b.color,
      noOfLeaves: b.no_of_leaves,
      paid: b.paid,
      monthlyLimit: String(b.monthly_limit),
      leavetype: b.leavetype,
      unusedLeave: b.unused_leave,
      encashed: b.unused_leave === "paid",
      allowedProbation: b.allowed_probation,
      allowedNotice: b.allowed_notice,
      overUtilization: b.over_utilization,
    })
    .$returningId();
  const newId = Number(ins!.id);

  // Seed a per-employee quota row so balances are live immediately (accrual cron tops up).
  const emps = await db.select({ id: users.id }).from(users).where(eq(users.companyId, companyId));
  if (emps.length)
    await db.insert(employeeLeaveQuotas).values(
      emps.map((e) => ({ userId: e.id, leaveTypeId: newId, noOfLeaves: b.no_of_leaves, leaveTypeImpact: false })),
    );
  res.json({ ok: true, id: newId, message: "Leave type created." });
});

/** PATCH /api/leave-types/:id */
leaveRouter.patch("/leave-types/:id", ...onlyManage, async (req, res) => {
  const companyId = req.auth!.companyId;
  const id = Number(req.params.id);
  const parsed = typeSchema.partial().safeParse(req.body);
  if (!parsed.success) return res.status(422).json({ error: "Invalid payload", details: parsed.error.flatten() });
  const b = parsed.data;
  const exists = await db.select({ id: leaveTypes.id }).from(leaveTypes).where(and(eq(leaveTypes.id, id), eq(leaveTypes.companyId, companyId))).limit(1);
  if (!exists[0]) return res.status(404).json({ error: "Leave type not found" });
  await db
    .update(leaveTypes)
    .set({
      ...(b.type_name !== undefined ? { typeName: b.type_name } : {}),
      ...(b.color !== undefined ? { color: b.color } : {}),
      ...(b.no_of_leaves !== undefined ? { noOfLeaves: b.no_of_leaves } : {}),
      ...(b.paid !== undefined ? { paid: b.paid } : {}),
      ...(b.monthly_limit !== undefined ? { monthlyLimit: String(b.monthly_limit) } : {}),
      ...(b.leavetype !== undefined ? { leavetype: b.leavetype } : {}),
      ...(b.unused_leave !== undefined ? { unusedLeave: b.unused_leave, encashed: b.unused_leave === "paid" } : {}),
      ...(b.allowed_probation !== undefined ? { allowedProbation: b.allowed_probation } : {}),
      ...(b.allowed_notice !== undefined ? { allowedNotice: b.allowed_notice } : {}),
      ...(b.over_utilization !== undefined ? { overUtilization: b.over_utilization } : {}),
    })
    .where(eq(leaveTypes.id, id));
  res.json({ ok: true, message: "Leave type updated." });
});

/** DELETE /api/leave-types/:id — soft delete (deletedAt) + drop quota rows. */
leaveRouter.delete("/leave-types/:id", ...onlyManage, async (req, res) => {
  const companyId = req.auth!.companyId;
  const id = Number(req.params.id);
  const exists = await db.select({ id: leaveTypes.id }).from(leaveTypes).where(and(eq(leaveTypes.id, id), eq(leaveTypes.companyId, companyId))).limit(1);
  if (!exists[0]) return res.status(404).json({ error: "Leave type not found" });
  const used = await db.select({ n: sql<number>`COUNT(*)` }).from(leaves).where(eq(leaves.leaveTypeId, id));
  if (Number(used[0]?.n ?? 0) > 0)
    return res.status(422).json({ error: "This leave type has leave records and can't be deleted. Archive it instead." });
  await db.delete(employeeLeaveQuotas).where(eq(employeeLeaveQuotas.leaveTypeId, id));
  await db.delete(leaveTypes).where(eq(leaveTypes.id, id));
  res.json({ ok: true, message: "Leave type deleted." });
});
